<template>
  <div>
    <v-row>
      <v-col
        cols="12"
        sm="8"
      >
        <member-selector
          class="no-print"
          :members="members"
          :groups="groups"
          :selected-member.sync="selectedMember"
          :selected-group.sync="selectedGroups"
          :selected-job-level.sync="selectedJobLevel"
        />
      </v-col>
      <v-col
        cols="12"
        sm="4"
      >
        <v-switch
          v-model="showActiveOnly"
          label="Show Active Only"
          class="no-print"
        />
        <v-switch
          v-model="useDateFilter"
          label="Use Date Filter"
          class="no-print"
        />
      </v-col>
    </v-row>

    <v-data-table
      :headers="headers"
      :items="filteredOffenses"
      group-by="member.fullName"
      item-key="id"
      :items-per-page="-1"
      show-expand
      dense
    >
      <template v-slot:group.header="{ items, isOpen, toggle }">
        <th colspan="7">
          <v-icon @click="toggle">
            {{ isOpen ? 'mdi-minus' : 'mdi-plus' }}
          </v-icon>
          <span style="font-size: large">
            {{ items[0].member.fullName || 'Others' }}
            ({{ summarizeOffenses(items) }})
            <template
              v-for="(count, noticeName) in summarizeNotices(items)"
            
            >
              <v-chip
                small
                class="ml-2"
                :color="getNoticeColor(count.level)"
                dark
                @click="viewNotice(count.notice)"
                style="cursor: pointer"
              >
                {{ noticeName }} {{ count.count }}x
              </v-chip>
            </template>
          </span>
        </th>
      </template>

      <template v-slot:item.effectiveDate="{ item }">
        {{ formatDate(item.effectiveDate) }}
      </template>

      <template v-slot:item.offenseType="{ item }">
        {{ item.offenseType.name }}
      </template>

      <template v-slot:item.noticeType="{ item }">
 
        <v-chip
          small
          v-if="item.penalty && item.penalty.noticeType && item.penalty.noticeType.name"
          :color="getNoticeColor(item.noticeLevel)"
          dark
          @click="viewNotice(item.notice)"
          style="cursor: pointer"
        >
          {{ item.penalty.noticeType.name }}
        </v-chip>
      </template>

      <template v-slot:item.attendances="{ item }">
        <div
          v-for="attendance in item.attendances"
          :key="attendance.id"
        >
          {{ formatDate(attendance.date) }} -
          <v-chip
            small
            :color="attendance.absent ? 'red darken-2' : 'orange'"
            dark
          >
            {{ attendance.absent ? 'Absent' : `Late ${Math.floor(attendance.lateHrs)? Math.floor(attendance.lateHrs) + 'h and': ''} ${Math.round((attendance.lateHrs % 1) * 60)}m` }}
          </v-chip>
        </div>
      </template>

      <template v-slot:item.actions="{ item }">
        <v-btn
          small
          color="error"
          @click="confirmDelete(item)"
          class="no-print"
        >
          <v-icon small>mdi-delete</v-icon>
        </v-btn>
      </template>

      <template v-slot:expanded-item="{ headers, item }">
        <td
          v-if="item.prevOffenses && item.prevOffenses.length"
          :colspan="headers.length"
        >
          <v-simple-table dense>
            <template v-slot:default>
              <thead>
                <tr>
                  <th>Effective Date</th>
                  <th>Offense Count</th>
                  <th>Penalty Days</th>
                  <th>Absent Days</th>
                  <th>Notice Type</th>
                  <th>Attendances</th>
                  <th class="no-print">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="offense in item.prevOffenses"
                  :key="offense.id"
                >
                  <td>{{ formatDate(offense.effectiveDate) }}</td>
                  <td>{{ offense.offenseCount }}</td>
                  <td>{{ offense.penaltyDays }}</td>
                  <td>{{ offense.absentDays }}</td>
                  <td>
                    <v-chip
                      small
                      :color="getNoticeColor(offense.penalty.noticeType.level)"
                      dark
                      @click="viewNotice(offense.notice)"
                      style="cursor: pointer"
                    >
                      {{ offense.penalty.noticeType.name }}
                    </v-chip>
                  </td>
                  <td>
                    <div
                      v-for="attendance in offense.attendances"
                      :key="attendance.id"
                    >
                      {{ formatDate(attendance.date) }} -
                      <v-chip
                        small
                        :color="attendance.absent ? 'red darken-2' : 'orange'"
                        dark
                      >
                        {{ attendance.absent ? 'Absent' : `Late ${Math.floor(attendance.lateHrs)? Math.floor(attendance.lateHrs) + 'h and': ''} ${Math.round((attendance.lateHrs % 1) * 60)}m` }}
                      </v-chip>
                    </div>
                  </td>
                  <td class="no-print">
                    <v-btn
                      small
                      color="error"
                      @click="confirmDelete(offense)"
                    >
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </td>
      </template>
    </v-data-table>

    <!-- Notice Dialog -->
    <v-dialog
      v-model="noticeDialog"
      max-width="900px"
    >
      <v-card>
        <v-card-title class="headline">
          Notice Details
          <v-spacer />
          <v-btn
            icon
            @click="print"
          >
            <v-icon>mdi-printer</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <div
            class="notice-content pa-4"
            v-html="selectedNotice.body"
          />
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            color="grey darken-1"
            text
            @click="closeNotice"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
  import { mapGetters, mapState } from 'vuex'
  import { Q_GET_EXISTING_OFFENSES, M_DELETE_OFFENSE } from '@/gql/offences'
  import moment from 'moment'
  import MemberSelector from '@/views/dashboard/components/MemberSelector'

  export default {
    name: 'OffenseReport',
    components: {
      MemberSelector,
    },
    data: () => ({
      headers: [
        { text: 'Effective Date', value: 'effectiveDate' },
        { text: 'Offense Type', value: 'offenseType.name' },
        { text: 'Offense Count', value: 'offenseCount' },
        { text: 'Penalty Days', value: 'penaltyDays' },
        { text: 'Absent Days', value: 'absentDays' },
        { text: 'Notice Type', value: 'noticeType' },
        { text: 'Attendances', value: 'attendances' },
        { text: 'Actions', value: 'actions', sortable: false },
      ],
      showActiveOnly: true,
      useDateFilter: true,
      selectedMember: [],
      selectedGroups: [],
      selectedJobLevel: [],
      offenses: [],
      selectedNotice: {},
      noticeDialog: false,
    }),

    computed: {
      ...mapState('records', ['startDate', 'endDate']),
      ...mapGetters(['team', 'groups']),

      filteredOffenses () {
        if (!this.offenses) return []
        return this.offenses
          .filter(o => {
            // Filter by currentTeam
            if (o.member.currentTeam !== this.team) return false

            // Filter by status
            if (this.showActiveOnly && o.status !== 'Active') return false

            // Filter by selected members/groups
            return (!this.selectedMember.length ||
              this.selectedMember.includes(o.memberId)) &&
              (!this.selectedGroups.length ||
                this.selectedGroups.includes(o.member.group)) &&
              (!this.selectedJobLevel.length || (o.member.jobLevel && this.selectedJobLevel.includes(o.member.jobLevel)))
          })
      },
    },

    methods: {
      formatDate (date) {
        return moment(date).format('DD MMM YYYY')
      },

      async confirmDelete (offense) {
        const res = await this.$confirm(
          `Are you sure you want to delete this offense record for ${offense.member.fullName}?`,
          {
            title: 'Delete Offense Record',
            color: 'warning',
          }
        )
        
        if (res) {
          try {
            await this.$apollo.mutate({
              mutation: M_DELETE_OFFENSE,
              variables: {
                where: {
                  id: offense.id,
                },
              },
            })
            
            // Refetch the data
            this.$apollo.queries.offenses.refetch()
          } catch (error) {
            console.error('Error deleting offense:', error)
            this.$toast.error('Failed to delete offense record')
          }
        }
      },

      getNoticeColor (level) {
        const colors = {
          1: 'warning',
          2: 'orange darken-2',
          3: 'red',
          4: 'red darken-4',
        }
        return colors[level] || 'grey'
      },

      summarizeOffenses (items) {
        const summary = items.reduce((acc, curr) => {
          const key = curr.offenseType.name
          if (!acc[key]) acc[key] = 0
          acc[key]++
          return acc
        }, {})

        return Object.entries(summary)
          .map(([type, count]) => `${type}: ${count}`)
          .join(', ')
      },

      summarizeNotices (items) {
        const notices = {}
        
        // Helper function to process an offense
        const processOffense = (offense) => {
          if (offense.notice && offense.noticeLevel) {
            // Handle direct notice on offense
            const noticeTypeName = offense.penalty && offense.penalty.noticeType && offense.penalty.noticeType.name || 
              'Level ' + offense.noticeLevel + ' Notice'
            if (!notices[noticeTypeName]) {
              notices[noticeTypeName] = {
                count: 0,
                level: offense.noticeLevel,
                notice: offense.notice,
              }
            }
            notices[noticeTypeName].count++
          }
          
          // Process previous offenses
          if (offense.prevOffenses) {
            offense.prevOffenses.forEach(function(prevOffense) {
              if (prevOffense.notice && prevOffense.noticeLevel) {
                const noticeTypeName = prevOffense.penalty && prevOffense.penalty.noticeType && prevOffense.penalty.noticeType.name || 
                  'Level ' + prevOffense.noticeLevel + ' Notice'
                if (!notices[noticeTypeName]) {
                  notices[noticeTypeName] = {
                    count: 0,
                    level: prevOffense.noticeLevel,
                    notice: prevOffense.notice,
                  }
                }
                notices[noticeTypeName].count++
              }
            })
          }
        }

        // Process each item
        items.forEach(processOffense)
        
        return notices
      },

      viewNotice (notice) {
        if (notice && notice.body) {
          this.selectedNotice = notice
          this.noticeDialog = true
        }
      },

      closeNotice () {
        this.noticeDialog = false
        this.selectedNotice = {}
      },

      print () {
        if (!this.selectedNotice.body) return
        
        const printWindow = window.open('', '_blank')
        printWindow.document.write(`
        <html>
          <head>
            <title>Notice Letter</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 20px; }
              @media print {
                body { padding: 0; }
              }
            </style>
          </head>
          <body>
            ${this.selectedNotice.body}
          </body>
        </html>
      `)
        printWindow.document.close()
        printWindow.print()
      },
    },

    apollo: {
      offenses: {
        query: Q_GET_EXISTING_OFFENSES,
        variables () {
          return {
            where: {
              ...(this.showActiveOnly ? { status: 'Active' } : {}),
              ...(this.useDateFilter ? {
                effectiveDate: {
                  $between: [
                    this.startDate,
                    moment(this.endDate).endOf('date').toDate(),
                  ],
                },
              } : {}),
            },
          }
        },
        update (data) {
          if (!data.offenses) return []

          // Group offenses by memberId and offenseTypeId
          const groupedOffenses = data.offenses.reduce((acc, offense) => {
            const key = `${offense.memberId}-${offense.offenseTypeId}`
            if (!acc[key]) {
              acc[key] = []
            }
            acc[key].push(offense)
            return acc
          }, {})

          console.log({ groupedOffenses })
          // For each group, sort by date and separate latest from previous
          return Object.values(groupedOffenses).reduce((acc, offenses) => {
            // Sort by effectiveDate descending
            const sorted = offenses.sort((a, b) =>
              moment(b.effectiveDate).valueOf() - moment(a.effectiveDate).valueOf(),
            )

            // Take the latest offense
            const latest = sorted[0]
            // Add all previous offenses to prevOffenses
            latest.prevOffenses = sorted.slice(1)
            console.log(latest)

            acc.push(latest)
            return acc
          }, [])
        },
        fetchPolicy: 'no-cache',
      },
    },
  }
</script>

<style scoped>
.v-data-table ::v-deep tbody tr:hover {
  background: transparent !important;
}

.notice-content {
  min-height: 300px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}
</style>
